import { designTokens } from "@itandi/itandi-bb-ui";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import Link from "next/link";
import type React from "react";
import type { ComponentProps } from "react";
import { ConstructionConfirmedList } from "~/components/common/constructions/confirmed_list/ConstructionList";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { DealEstimationHeaderStatus } from "~/components/common/deal_estimation_headers/DealEstimationHeaderStatus";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientDealLayout } from "~/components/common/layouts/deals/ClientDealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { useDealEstimationConfirmedHeaders } from "~/hooks/deals/useDealEstimationConfirmedHeaders";
import { useDealEstimationHeaders } from "~/hooks/deals/useDealEstimationHeaders";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { isOwnStatus } from "~/models/Status";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH } from "~/pages/deals/[id]";
import { getDownloadPath } from "~/utils/api/deals/DealEstimationHistoryConfirmedFile";
import Styles from "./Page.module.scss";

export const Page: React.FC = () => {
	const id = useIdQueryHandler();
	const headerId = useIdQueryHandler("headerId");
	const { data: deal } = useDeals().show(id);
	const { data: dealEstimationHeaders } = useDealEstimationHeaders().index(id);
	const { data: dealEstimationHeaderContext } =
		useDealEstimationConfirmedHeaders().show(id, headerId);
	if (
		id === null ||
		headerId === null ||
		deal === null ||
		dealEstimationHeaders === null ||
		dealEstimationHeaderContext === null
	) {
		return <Loading isLoading />;
	}
	const dealEstimationHeader =
		dealEstimationHeaders.find((header) => header.id === headerId) ?? null;
	const handleClickPreview = (): void => {
		const path = getDownloadPath(id, headerId);
		window.open(path);
	};
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "見積り履歴確認" }
	] as const satisfies ComponentProps<typeof ClientDealLayout>["items"];
	if (dealEstimationHeader === null) return null;
	return (
		<DefaultV2>
			<ClientDealLayout deal={deal} items={items}>
				<div className={Styles.EstimateHeader}>
					<h1 className={Styles.EstimateHeaderTitle}>見積り履歴確認</h1>
					<DealEstimationHeaderStatus
						dealEstimationHeader={dealEstimationHeader}
						ownManagement={isOwnStatus(deal.statusId)}
					/>
				</div>
				<ConstructionTop
					constructionContext={dealEstimationHeaderContext}
					handleClickEstimationHistoryPreview={handleClickPreview}
					readonly
				/>
				<ConstructionConfirmedList
					companyType="client"
					constructionContext={dealEstimationHeaderContext}
				/>
				{deal.estimationBy === "vendor" && (
					<Box mt={designTokens["spacing/l"]}>
						<Panel>
							<PanelHeader title="備考" />
							<PanelBody>{dealEstimationHeader.note}</PanelBody>
						</Panel>
					</Box>
				)}
			</ClientDealLayout>
		</DefaultV2>
	);
};
